import { useEffect, useState, useCallback, useRef } from 'react';
import { match } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { withLoading } from 'utils';
import { changeLocale } from 'actions/statusActions';

import { useSelector } from 'react-redux';

import classes from './Register.module.scss';
import RegisterForm from './RegisterForm/RegisterForm';
import RegisterHeader from './RegisterHeader';
import api from 'api';


const stripHtml = (value: string) => value.replace(/<[^>]*>/g, '').trim();

const getTextFromTranslation = (value: any, fallback = ''): string => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (!value) {
    return fallback;
  }

  if (typeof value === 'object') {
    if (typeof value.stringValue === 'string') {
      return value.stringValue;
    }

    if (typeof value.prettyValue === 'string') {
      return stripHtml(value.prettyValue);
    }

    const html = value?.props?.dangerouslySetInnerHTML?.__html;
    if (typeof html === 'string') {
      return stripHtml(html);
    }

    const children = value?.props?.children;
    if (typeof children === 'string' || typeof children === 'number') {
      return String(children);
    }

    if (Array.isArray(children)) {
      const text = children
        .map((child) => getTextFromTranslation(child, ''))
        .join('')
        .trim();

      return text || fallback;
    }
  }

  return fallback;
};

type FinishPaidRegistrationProps = {
  history: any;
  match: match<{ pendingRegistration: string }>;
  internationalCustomers?: boolean;
};

const FinishPaidRegistration = ({ match, history, internationalCustomers = false }: FinishPaidRegistrationProps) => {
  const dispatch = useDispatch();

  const [processing, setProcessing] = useState(true);
  const [pendingRegistration, setPendingRegistration] = useState<any>();
  const [locations, setLocations] = useState<Partial<string[]>>();
  const [onePersonProduct, setOnePersonProduct] = useState(false);
  const [registrationLocale, setRegistrationLocale] = useState('en');

  const rawTranslate = useSelector((state: any) => state.status.translate);
  const currentLanguageCode = useSelector((state: any) => state.status.lang?.code || state.status.lang || 'en');

  const rawTranslateRef = useRef(rawTranslate);
  const currentLanguageCodeRef = useRef(currentLanguageCode);

  useEffect(() => {
    rawTranslateRef.current = rawTranslate;
  }, [rawTranslate]);

  useEffect(() => {
    currentLanguageCodeRef.current = currentLanguageCode;
  }, [currentLanguageCode]);

  const t = useCallback((id: number, fallback: string) => {
    const activeTranslate = rawTranslateRef.current;

    if (typeof activeTranslate === 'function') {
      return getTextFromTranslation(activeTranslate(id, fallback, true), fallback);
    }

    console.log('[FinishPaidRegistration][translate.invalid]', {
      type: typeof activeTranslate,
      fallback,
    });

    return fallback;
  }, []);

  const [isAutoFinish, setIsAutoFinish] = useState(false);
  const [autoError, setAutoError] = useState<string | null>(null);
  const [productName, setProductName] = useState<string | null>(null);

  const [precheckError, setPrecheckError] = useState<string | null>(null);

  const [noPlansDialog, setNoPlansDialog] = useState<{ open: boolean; buyUrl: string }>({
    open: false,
    buyUrl: '',
  });

  const pendingId = match.params.pendingRegistration;

  // guard against double-run in React 18 StrictMode (dev)
  const didAutoFinishRef = useRef(false);

  // reset guard ONLY when pendingId changes (not on locale/translate changes)
  useEffect(() => {
    didAutoFinishRef.current = false;
  }, [pendingId]);

  const runAutoFinish = useCallback(
    async (id: string) => {
      try {
        setIsAutoFinish(true);
        setAutoError(null);

        console.log('[FinishPaidRegistration][auto_finish.start]', { pendingId: id });

        const resp = await withLoading(dispatch, async () => {
          return api.pendingRegistration.finishRegistration(id, {});
        });

        console.log('[FinishPaidRegistration][auto_finish.done]', {
          pendingId: id,
          success: resp?.success,
          redirectTo: resp?.redirectTo,
          error: resp?.error,
        });

        if (resp?.success && resp?.redirectTo) {
          window.location.href = resp.redirectTo;
          return;
        }

        setAutoError(resp?.error || t(9993, 'Unable to continue'));
      } catch (e: any) {
        console.log('[FinishPaidRegistration][auto_finish.exception]', e?.message || e);
        setAutoError(t(9993, 'Unable to continue'));
      }
    },
    [dispatch, t],
  );

  useEffect(() => {
    const validate = async (id: string) => {
      try {
        setProcessing(true);
        setIsAutoFinish(false);
        setAutoError(null);
        setPrecheckError(null);
        setNoPlansDialog({ open: false, buyUrl: '' });

        const result = await api.pendingRegistration.getById(id);

        if (!result?.success) {
          alert(result?.error || 'Something goes wrong');
          return history.push('/');
        }

        const pr = result.data;
        const landingPage = pr?.landingPageId && typeof pr.landingPageId === 'object'
          ? pr.landingPageId
          : null;
        const landingLocale = String(landingPage?.defaultLocale || 'en').trim() || 'en';

        if (internationalCustomers) {
          setRegistrationLocale(landingLocale);

          if (currentLanguageCodeRef.current !== landingLocale) {
            currentLanguageCodeRef.current = landingLocale;
            (dispatch as any)(changeLocale(landingLocale));
          }
        }

        if (!pr?.isPaid) {
          alert('Not paid');
          return history.push('/');
        }

        if (pr?.isFinished) {
          console.log('[FinishPaidRegistration][already_finished]', { pendingId: id });

          if (!didAutoFinishRef.current) {
            didAutoFinishRef.current = true;
            await runAutoFinish(id);
          }

          setProcessing(false);
          return;
        }

        console.log('[FinishPaidRegistration][loaded_pr]', {
          pendingId: id,
          email: pr?.email,
          learningCenterId: pr?.learningCenterId ? String(pr.learningCenterId) : null,
          purchaseLcId: pr?.purchaseId?.learningCenterId ? String(pr.purchaseId.learningCenterId) : null,
          productId: pr?.productId ? String(pr.productId) : null,
        });

        if (pr?.productId) {
          try {
            const r = await api.products.getProductName(pr.productId);
            const name = r?.productName || null;

            console.log('[FinishPaidRegistration][product_name]', {
              pendingId: id,
              productId: String(pr.productId),
              productName: name,
            });

            setProductName(name);
          } catch (e: any) {
            console.log('[FinishPaidRegistration][product_name.fail]', e?.message || e);
          }
        }

        if (pr?.productId) {
          const product = await api.onePersonProduct.getOnePersonByParams({ _id: pr.productId });
          setOnePersonProduct(!!product?.onePerson);
        }

        setPendingRegistration(pr);
        setLocations(result.locations);

        const email = String(pr?.email || '').trim();
        const lcIdFromPr = pr?.learningCenterId ? String(pr.learningCenterId) : '';
        const lcIdFromPurchase = pr?.purchaseId?.learningCenterId ? String(pr.purchaseId.learningCenterId) : '';
        const learningCenterId = lcIdFromPr || lcIdFromPurchase;

        // EXISTING EMAIL FIRST
        if (email) {
          const status = await api.pendingRegistration.emailStatus(
            learningCenterId ? { email, learningCenterId } : { email },
          );

          console.log('[FinishPaidRegistration][email_status]', {
            pendingId: id,
            email,
            learningCenterId: learningCenterId || null,
            success: status?.success,
            exists: status?.exists,
            belongs: status?.belongs,
            matchesLearningCenter: status?.matchesLearningCenter,
          });

          const exists = !!status?.exists;
          const belongs = status?.belongs === true || status?.matchesLearningCenter === true;

          if (exists) {
            console.log('[FinishPaidRegistration][existing_user_auto_finish]', {
              pendingId: id,
              email,
              learningCenterId: learningCenterId || null,
              belongs,
              reason: belongs ? 'existing_email_same_lc_or_no_lc' : 'existing_email_lc_mismatch_but_paid_flow',
            });

            setProcessing(false);

            if (!didAutoFinishRef.current) {
              didAutoFinishRef.current = true;
              await runAutoFinish(id);
            }

            return;
          }
          
        }

        // ONLY NEW EMAIL GOES THROUGH PRECHECK
        const pre = await api.pendingRegistration.precheck(id);

        console.log('[FinishPaidRegistration][precheck]', {
          pendingId: id,
          success: pre?.success,
          error: pre?.error,
          errorCode: pre?.errorCode,
        });

        if (!pre?.success) {
          if (pre?.errorCode === 'NO_PLANS') {
            const buyUrl =
              typeof pre?.buyUrl === 'string' && pre.buyUrl.trim()
                ? pre.buyUrl.trim()
                : 'https://lp.familylearningcompany.com/website/checkout';

            console.log('[FinishPaidRegistration][NO_PLANS]', {
              pendingId: id,
              buyUrl,
              error: pre?.error,
            });

            setProcessing(false);
            setNoPlansDialog({ open: true, buyUrl });
            return;
          }

          setProcessing(false);
          setPrecheckError(pre?.error || t(9993, 'Unable to continue'));
          return;
        }

        // NEW EMAIL => show registration form
        setProcessing(false);
        setIsAutoFinish(false);
      } catch (e: any) {
        console.log('[FinishPaidRegistration][validate.exception]', e?.message || e);
        setProcessing(false);
        setPrecheckError(t(9993, 'Unable to continue'));
      }
    };

    validate(pendingId);
  }, [dispatch, history, internationalCustomers, pendingId, runAutoFinish, t]);

  return (
    <div className={classes.wrapper}>
      <RegisterHeader />

      <main className={classes.main}>
        {processing && <p>{t(9994, 'Processing...')}</p>}

        {!processing && noPlansDialog.open && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
            <div
              style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }}
            />
            <div
              role="dialog"
              aria-modal="true"
              style={{
                position: 'absolute',
                left: '50%',
                top: '50%',
                transform: 'translate(-50%, -50%)',
                background: '#fff',
                color: '#111',
                borderRadius: 12,
                padding: 18,
                width: 'min(560px, calc(100vw - 32px))',
                boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
              }}
            >
              <div style={{ fontSize: 16, lineHeight: 1.4 }}>
                {t(
                  3668,
                  'There are no family plans left. You can contact someone in your organization and ask them to order more, or click the BUY button to purchase a product yourself.',
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14, gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setNoPlansDialog({ open: false, buyUrl: '' })}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(17,17,17,0.15)',
                    borderRadius: 9999,
                    padding: '10px 14px',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>

                <button
                  type="button"
                  onClick={() => {
                    const url =
                      (noPlansDialog.buyUrl || '').toString().trim() ||
                      'https://lp.familylearningcompany.com/website/checkout';
                    console.log('[FinishPaidRegistration][NO_PLANS][buy]', { url });
                    window.location.href = url;
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px 16px',
                    borderRadius: 9999,
                    border: '1px solid rgba(17, 17, 17, 0.08)',
                    background: '#7c3aed',
                    color: '#fff',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Buy
                </button>
              </div>
            </div>
          </div>
        )}

        {!processing && isAutoFinish && (
          <div>
            <h1>Thank you for your purchase</h1>
            {productName && <p>{productName}</p>}
            <p>Activating your access…</p>
            {autoError && <p style={{ marginTop: 12, color: '#b91c1c' }}>{autoError}</p>}
          </div>
        )}

        {!processing && !isAutoFinish && precheckError && (
          <div>
            <h1>{t(9993, 'Unable to continue')}</h1>
            <p>{precheckError}</p>
          </div>
        )}

        {!processing && pendingRegistration && !isAutoFinish && !precheckError && (
          <RegisterForm
            initialValues={{
              email: pendingRegistration.email,
              messageLocale: registrationLocale,
              onePersonProduct,
            }}
            disabledFields={{ email: true }}
            internationalRegistration={internationalCustomers}
            forcedMessageLocale={internationalCustomers ? registrationLocale : undefined}
            submit={(values) =>
              withLoading(dispatch, async () => {
                return api.pendingRegistration.finishRegistration(pendingId, values);
              })
            }
            locations={locations}
          />
        )}
      </main>
    </div>
  );
};

export default FinishPaidRegistration;